import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/index.js';
import { API_BASE_URL } from '../config.js';
import { useToast } from '../context/ToastContext.js';
import './SkillTracker.css';

const STATUS_LABELS = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  achieved: 'Achieved',
  mastered: 'Mastered'
};

const STATUS_COLORS = {
  not_started: '#bdc3c7',
  in_progress: '#e67e22',
  achieved: '#27ae60',
  mastered: '#8e44ad'
};

function SkillTracker() {
  const toast = useToast();
  const { athleteId } = useParams();
  const navigate = useNavigate();
  const [grouped, setGrouped] = useState({});
  const [athlete, setAthlete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeApparatus, setActiveApparatus] = useState(null);
  const [editingSkill, setEditingSkill] = useState(null);
  const [saving, setSaving] = useState(false);

  // Athlete picker state — used when no athleteId in URL
  const [allAthletes, setAllAthletes] = useState([]);
  const [pickingAthlete, setPickingAthlete] = useState(!athleteId);

  let user = null;
  try { user = JSON.parse(localStorage.getItem('user')); } catch {}
  const userRole = user?.role;
  const canEdit = userRole === 'admin' || userRole === 'coach';

  // Load athlete list when showing picker
  // GET /athletes is role-scoped at the backend:
  //   admin/coach → full team, parent → linked children, athlete → self
  useEffect(() => {
    if (!athleteId) {
      api.get(`${API_BASE_URL}/athletes`)
        .then(res => {
          const list = res.data || [];
          setAllAthletes(list);
          // Athletes only have themselves — skip the picker and go straight to their page
          if (userRole === 'athlete' && list.length === 1) {
            navigate(`/skills/${list[0].id}`, { replace: true });
          }
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [athleteId, userRole, navigate]);

  const fetchSkills = useCallback(async () => {
    try {
      setLoading(true);
      const [skillsRes, athleteRes] = await Promise.all([
        api.get(`${API_BASE_URL}/api/athletes/${athleteId}/skills`),
        api.get(`${API_BASE_URL}/athletes/${athleteId}`)
      ]);
      setGrouped(skillsRes.data.grouped || {});
      setAthlete(athleteRes.data);
      const apparatusList = Object.keys(skillsRes.data.grouped || {});
      if (apparatusList.length > 0 && !activeApparatus) {
        setActiveApparatus(apparatusList[0]);
      }
    } catch (err) {
      console.error('Error loading skills:', err);
    } finally {
      setLoading(false);
    }
  }, [athleteId, activeApparatus]);

  useEffect(() => { if (athleteId) fetchSkills(); }, [athleteId]);

  const saveProgress = async (skillId, updates) => {
    try {
      setSaving(true);
      await api.post(`${API_BASE_URL}/api/athletes/${athleteId}/skills/${skillId}`, updates);
      setEditingSkill(null);
      fetchSkills();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const apparatus = activeApparatus ? (grouped[activeApparatus] || []) : [];

  const getCompletion = (skills) => {
    if (!skills.length) return 0;
    const done = skills.filter(s => s.status === 'achieved' || s.status === 'mastered').length;
    return Math.round((done / skills.length) * 100);
  };

  return (
    <div className="skill-tracker-container">
      <div className="skill-tracker-header">
        <button className="back-btn" onClick={() => navigate(-1)}>← Back</button>
        <div>
          <h1>Skill Tracker</h1>
          {athlete && <div className="athlete-subtitle">{athlete.name}</div>}
        </div>
      </div>

      {/* Athlete picker — shown when no athleteId in URL */}
      {!athleteId && (
        <div style={{ padding: '1rem 0' }}>
          <p style={{ color: '#6b7280', marginBottom: '1rem', fontSize: '0.95rem' }}>
            {userRole === 'parent' ? 'Select a child to view their skill progress:' : 'Select an athlete to view their skill progress:'}
          </p>
          {loading ? (
            <div className="loading-state">Loading athletes...</div>
          ) : allAthletes.length === 0 ? (
            <p style={{ color: '#6b7280' }}>No athletes found. Add athletes first.</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.75rem' }}>
              {allAthletes.map(a => (
                <button
                  key={a.id}
                  onClick={() => navigate(`/skills/${a.id}`)}
                  style={{
                    padding: '1rem',
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontWeight: '600',
                    fontSize: '1rem',
                    transition: 'box-shadow 0.15s'
                  }}
                  onMouseOver={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'}
                  onMouseOut={e => e.currentTarget.style.boxShadow = 'none'}
                >
                  🏅 {a.name}
                  {a.level && <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.25rem' }}>Level {a.level}</div>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Skill detail — shown when athleteId is present */}
      {athleteId && (
        <>
          {/* Apparatus tabs */}
          <div className="apparatus-tabs">
            {Object.keys(grouped).map(app => {
              const pct = getCompletion(grouped[app]);
              return (
                <button
                  key={app}
                  className={`apparatus-tab ${activeApparatus === app ? 'active' : ''}`}
                  onClick={() => setActiveApparatus(app)}
                >
                  <span className="apparatus-name">{app}</span>
                  <span className="apparatus-pct">{pct}%</span>
                </button>
              );
            })}
          </div>

          {/* Progress bar */}
          {activeApparatus && (
            <div className="apparatus-progress">
              <div className="progress-bar-label">
                <span>{activeApparatus} — {getCompletion(apparatus)}% complete</span>
                <span>{apparatus.filter(s => ['achieved','mastered'].includes(s.status)).length}/{apparatus.length} skills</span>
              </div>
              <div className="progress-bar-outer">
                <div
                  className="progress-bar-inner"
                  style={{ width: `${getCompletion(apparatus)}%` }}
                />
              </div>
            </div>
          )}

          {/* Skills list */}
          <div className="skills-list">
            {apparatus.map(skill => (
              <div
                key={skill.id}
                className={`skill-row ${skill.status || 'not_started'}`}
              >
                <div className="skill-info">
                  <div className="skill-name">
                    {skill.skill_name}
                    {skill.level_required && (
                      <span className="skill-level-tag">{skill.level_required}</span>
                    )}
                  </div>
                  {skill.description && (
                    <div className="skill-description">{skill.description}</div>
                  )}
                </div>

                <div className="skill-status-area">
                  {editingSkill?.id === skill.id ? (
                    <SkillEditForm
                      skill={skill}
                      onSave={(updates) => saveProgress(skill.id, updates)}
                      onCancel={() => setEditingSkill(null)}
                      saving={saving}
                    />
                  ) : (
                    <>
                      <span
                        className="status-pill"
                        style={{ background: STATUS_COLORS[skill.status || 'not_started'] }}
                      >
                        {STATUS_LABELS[skill.status || 'not_started']}
                      </span>
                      {skill.coach_rating > 0 && (
                        <span className="coach-rating">
                          {'★'.repeat(skill.coach_rating)}{'☆'.repeat(5 - skill.coach_rating)}
                        </span>
                      )}
                      {canEdit && (
                        <button
                          className="btn-edit-skill"
                          onClick={() => setEditingSkill(skill)}
                        >
                          Edit
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function SkillEditForm({ skill, onSave, onCancel, saving }) {
  const [status, setStatus] = useState(skill.status || 'not_started');
  const [rating, setRating] = useState(skill.coach_rating || 0);
  const [notes, setNotes] = useState(skill.progress_notes || '');

  return (
    <div className="skill-edit-form">
      <select value={status} onChange={e => setStatus(e.target.value)}>
        <option value="not_started">Not Started</option>
        <option value="in_progress">In Progress</option>
        <option value="achieved">Achieved</option>
        <option value="mastered">Mastered</option>
      </select>
      <div className="rating-input">
        {[1,2,3,4,5].map(n => (
          <span
            key={n}
            className={`star ${n <= rating ? 'filled' : ''}`}
            onClick={() => setRating(n)}
          >★</span>
        ))}
      </div>
      <input
        type="text"
        value={notes}
        onChange={e => setNotes(e.target.value)}
        placeholder="Coach notes..."
        className="notes-input"
      />
      <div className="edit-form-actions">
        <button className="btn-cancel-small" onClick={onCancel}>Cancel</button>
        <button
          className="btn-save-small"
          disabled={saving}
          onClick={() => onSave({ status, coach_rating: rating, notes })}
        >
          {saving ? '...' : 'Save'}
        </button>
      </div>
    </div>
  );
}

export default SkillTracker;
